<div class="flex flex-col gap-4" [class.w-full]="!embedded()" [class.max-w-5xl]="!embedded()" [class.mx-auto]="!embedded()" [class.pb-8]="!embedded()">
  @if (loading() && !transaction()) {
    <div class="flex flex-col items-center justify-center py-12 gap-4">
      <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
      <p class="text-sm text-slate-600">Cargando detalle...</p>
    </div>
  } @else if (notFound()) {
    <div class="rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
      <p class="text-slate-700 font-medium m-0">No se encontró la transacción.</p>
      @if (!embedded()) {
        <a routerLink="/mit-transactions" class="text-sm text-sky-700 hover:underline">Volver a Transacciones bajo demanda</a>
      }
    </div>
  } @else if (transaction(); as tx) {
    @if (!embedded()) {
      <a routerLink="/mit-transactions" class="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 w-fit">
        <i class="pi pi-arrow-left text-xs"></i>
        <span>Transacciones bajo demanda</span>
      </a>
    }

    @if (tx.type === 'CARD_VERIFICATION') {
      <div class="rounded-xl border px-4 py-3 text-sm shadow-sm"
        [class]="tx.status === 'VOID_FAILED' ? 'border-red-200 bg-red-50 text-red-800' : 'border-slate-200 bg-white text-slate-700'">
        @switch (tx.status) {
          @case ('VOIDED') {
            Cargo de validación cancelado el {{ tx.voidedAt | date:'dd/MM/yyyy HH:mm' }}. No genera venta.
          }
          @case ('VOID_PENDING') {
            Cargo de validación aprobado; la cancelación se reintenta automáticamente hasta el
            {{ tx.voidDeadlineAt | date:'dd/MM/yyyy HH:mm' }} ({{ tx.voidAttempts ?? 0 }} intento(s)).
            @if (tx.voidError) { Último error: {{ tx.voidError }} }
          }
          @case ('VOID_FAILED') {
            No se pudo cancelar el cargo de validación en 72 horas. Cancélalo o devuélvelo en Paycode y confírmalo aquí.
            @if (tx.voidError) { Último error: {{ tx.voidError }} }
          }
          @default {
            Cargo de validación al registrar la tarjeta; si se aprueba se cancela y no genera venta.
          }
        }
      </div>
    }

    @if (tx.status === 'PENDING_RECONCILIATION') {
      <div class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
        No hubo respuesta clara del banco. No se debe volver a cobrar hasta que Pento confirme el resultado con el proveedor.
      </div>
    }
    @if (tx.status === 'REJECTED') {
      <div class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 shadow-sm">
        Rechazada antes de enviarse al banco: {{ rejection() }}.
      </div>
    }

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
          <i class="pi pi-credit-card text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Información general</span>
      </div>
      <div class="p-4">
        <div class="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-slate-900 truncate">{{ tx.concept || typeLabel() }}</p>
            <div class="flex flex-wrap gap-1 mt-1">
              <p-tag class="!text-[10px]" [severity]="statusSeverity(tx.status)" [value]="statusLabel()" />
              <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{{ typeLabel() }}</span>
            </div>
          </div>
          <span class="text-sm font-bold text-slate-900 shrink-0 tabular-nums">
            {{ (tx.amount ?? 0) | currency:(tx.currencyCode || 'MXN'):'symbol':'1.2-2' }}
          </span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <p class="text-xs font-medium text-slate-500">Fecha</p>
            <p class="text-sm text-slate-700">{{ tx.createdAt ? (tx.createdAt | date:'dd/MM/yyyy HH:mm:ss') : '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Cliente</p>
            @if (tx.customerId) {
              <button
                type="button"
                class="inline-flex items-center gap-1.5 mt-0.5 text-sm text-sky-600 hover:text-sky-800"
                (click)="openCustomerDetails(tx.customerId)">
                <i class="pi pi-eye text-[10px] shrink-0"></i>
                <span>{{ tx.customerName || 'Ver cliente' }}</span>
              </button>
            } @else {
              <p class="text-sm text-slate-700">{{ tx.customerName || '—' }}</p>
            }
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Mandato</p>
            @if (canReadMandate && tx.mandateId) {
              <a [routerLink]="['/mit-mandates', tx.mandateId]" class="text-sm text-sky-600 hover:text-sky-800">{{ tx.mandateNumber }}</a>
            } @else {
              <p class="text-sm text-slate-700">{{ tx.mandateNumber || '—' }}</p>
            }
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Tarjeta</p>
            <p class="text-sm text-slate-700 font-mono">{{ tx.cardLastDigits ? '•••• ' + tx.cardLastDigits : '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Referencia externa</p>
            <p class="text-sm text-slate-700 break-all">{{ tx.externalReference || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Solicitado por</p>
            <p class="text-sm text-slate-700">{{ requestedBy() }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Venta</p>
            @if (tx.saleId) {
              <a [routerLink]="['/sales', tx.saleId]" class="text-sm text-sky-600 hover:text-sky-800">{{ tx.saleNumber || tx.saleId }}</a>
            } @else {
              <p class="text-sm text-slate-700">—</p>
            }
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Pago</p>
            @if (tx.paymentId) {
              <a routerLink="/payments" [queryParams]="{ id: tx.paymentId }" class="text-sm text-sky-600 hover:text-sky-800">#{{ tx.paymentId }}</a>
            } @else {
              <p class="text-sm text-slate-700">—</p>
            }
          </div>
          @if (tx.materializationError) {
            <div class="sm:col-span-2">
              <p class="text-xs font-medium text-slate-500">Registro de la venta</p>
              <p class="text-sm text-amber-700">{{ tx.materializationError }}</p>
            </div>
          }
        </div>
        @if (showRetryVoid() || showResolve()) {
          <div class="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">
            @if (showRetryVoid()) {
              <p-button label="Reintentar cancelación" icon="pi pi-refresh" size="small" severity="secondary" [loading]="voiding()"
                (onClick)="retryVoid()" />
            }
            @if (showResolve()) {
              <p-button [label]="voidOpen() ? 'Confirmar cancelación' : 'Resolver'" icon="pi pi-check-square" size="small"
                (onClick)="resolveOpen.set(true)" />
            }
          </div>
        }
      </div>
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
          <i class="pi pi-tags text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Metadata comercial</span>
      </div>
      <div class="p-4">
        <app-mit-comercial-metadata-view [metadata]="tx.comercialMetadata" [showTitle]="false"
          hint="Enviada a Paycode en este cargo, junto con los identificadores de Pento." />
      </div>
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
          <i class="pi pi-building text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Proveedor</span>
      </div>
      <div class="p-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <p class="text-xs font-medium text-slate-500">Código</p>
            <p class="text-sm text-slate-700">{{ tx.providerCode || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Referencia</p>
            <p class="text-sm text-slate-700 break-all">{{ tx.providerReference || '—' }}</p>
          </div>
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Mensaje</p>
            <p class="text-sm text-slate-700">{{ tx.providerMessage || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Respondió</p>
            <p class="text-sm text-slate-700">{{ tx.providerRespondedAt ? (tx.providerRespondedAt | date:'dd/MM/yyyy HH:mm:ss') : '—' }}</p>
          </div>
          @if (tx.resolvedAt) {
            <div>
              <p class="text-xs font-medium text-slate-500">Resuelta</p>
              <p class="text-sm text-slate-700">
                {{ tx.resolvedAt | date:'dd/MM/yyyy HH:mm' }}{{ tx.resolutionSource === 'MANUAL' ? ' (manual)' : '' }}
              </p>
            </div>
          }
          @if (tx.resolutionNote) {
            <div class="sm:col-span-2">
              <p class="text-xs font-medium text-slate-500">Nota de resolución</p>
              <p class="text-sm text-slate-700">{{ tx.resolutionNote }}</p>
            </div>
          }
        </div>
      </div>
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
          <i class="pi pi-history text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Historial</span>
      </div>
      <div class="px-4 py-3">
        <app-collection-attempt-timeline [events]="events()" [loading]="eventsLoading()" />
      </div>
    </div>

    @if (tx.lines; as lines) {
      @if (lines.length) {
      <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
          <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
            <i class="pi pi-list text-sm"></i>
          </span>
          <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Productos ({{ lines.length }})</span>
        </div>
        <div class="divide-y divide-slate-100">
          @for (line of lines; track $index) {
            <div class="flex items-center gap-2 px-4 py-2">
              <div class="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-sky-600 [&>i]:text-xs">
                <i class="pi pi-box"></i>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs font-medium text-slate-900 truncate">{{ line.description || 'Sin descripción' }}</p>
                <p class="text-[11px] text-slate-500 mt-0.5">
                  {{ line.quantity }} × {{ line.unitPrice | currency:(tx.currencyCode || 'MXN'):'symbol':'1.2-2' }}
                  · Impuestos {{ (line.taxAmount ?? 0) | currency:(tx.currencyCode || 'MXN'):'symbol':'1.2-2' }}
                </p>
              </div>
              <span class="text-xs font-bold text-slate-900 shrink-0 tabular-nums">
                {{ (line.totalAmount ?? 0) | currency:(tx.currencyCode || 'MXN'):'symbol':'1.2-2' }}
              </span>
            </div>
          }
        </div>
      </div>
      }
    }

    @if (evaluation(); as ev) {
      <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
          <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
            <i class="pi pi-chart-bar text-sm"></i>
          </span>
          <div class="min-w-0">
            <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Evaluación de límites</span>
            <p class="text-[11px] text-slate-500 m-0">
              {{ evaluationResult() }} · {{ ev.evaluatedAt | date:'dd/MM/yyyy HH:mm:ss' }}
            </p>
          </div>
        </div>
        <div class="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-xs text-slate-500">
                <th class="text-left font-medium pb-2">Límite aplicado</th>
                <th class="text-right font-medium pb-2">Valor</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              @for (row of limitRows; track row.key) {
                <tr>
                  <td class="py-1.5 text-slate-600">{{ row.label }}</td>
                  <td class="py-1.5 text-right text-slate-900">{{ limitValue(row, ev.limits) }}</td>
                </tr>
              }
            </tbody>
          </table>
          <table class="w-full text-sm">
            <thead>
              <tr class="text-xs text-slate-500">
                <th class="text-left font-medium pb-2">Consumo previo</th>
                <th class="text-right font-medium pb-2">Valor</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              @for (row of usageRows(); track row.label) {
                <tr>
                  <td class="py-1.5 text-slate-600">{{ row.label }}</td>
                  <td class="py-1.5 text-right text-slate-900">{{ row.value }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  }
</div>

<app-mit-transaction-resolve-dialog
  [transaction]="transaction()"
  [visible]="resolveOpen()"
  (closed)="resolveOpen.set(false)"
  (resolved)="onResolved($event)" />

<p-drawer
  [visible]="isCustomerDetailsOpen()"
  position="right"
  appendTo="body"
  (visibleChange)="onCustomerDrawerVisibleChange($event)"
  styleClass="mit-tx-customer-drawer !w-[800px]"
  [blockScroll]="true">
  <ng-template pTemplate="header">
    <div class="flex items-center justify-between w-full gap-3">
      <div class="flex items-center gap-3 min-w-0">
        <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600 shadow-sm">
          <i class="pi pi-user text-base"></i>
        </span>
        <h3 class="text-lg font-semibold text-slate-900">Detalles del cliente</h3>
      </div>
      @if (selectedCustomer() && !customerDetailsLoading()) {
        <button
          type="button"
          class="inline-flex items-center gap-2 text-sm font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shrink-0"
          (click)="editCustomerFromModal()">
          <i class="pi pi-pencil text-sm"></i>
          Editar
        </button>
      }
    </div>
  </ng-template>
  @if (customerDetailsLoading()) {
    <div class="flex flex-col items-center justify-center py-12 gap-4">
      <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
      <p class="text-sm text-slate-600">Cargando cliente...</p>
    </div>
  } @else if (selectedCustomer()) {
    <app-customer-read
      [customer]="selectedCustomer()!"
      (openSale)="openSaleFromCustomer($event)" />
  }
</p-drawer>
